(() => {
  'use strict';
  if (window.UniHomeV2) return;
  const query = (selector, scope = document) => scope.querySelector(selector);
  const all = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const feeds = new Map();
  const mountedTabs = new Map();
  const carts = new WeakMap();
  const root = window.Shopify?.routes?.root || '/';

  function previewURL(input) {
    const url = new URL(input, location.href);
    if (url.origin !== location.origin) throw new Error('Unexpected storefront URL');
    const current = new URL(location.href);
    for (const name of ['preview_theme_id', 'uni_preview']) {
      if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
    }
    return window.UniThemePreview?.url(url.href) || url.href;
  }

  function setFeedControls(state) {
    const canLoad = state.loads < 3 && Boolean(state.queue.length || state.next);
    state.button.hidden = !canLoad;
    state.link.hidden = canLoad;
    state.button.disabled = state.busy;
    state.button.setAttribute('aria-busy', String(state.busy));
    state.button.textContent = state.busy ? state.element.dataset.loadingLabel : state.label;
    state.empty.hidden = Boolean(state.grid.children.length || state.next || state.queue.length);
    state.element.dataset.successfulLoads = String(state.loads);
  }

  async function fetchFeedPage(state) {
    const url = new URL(previewURL(state.next));
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', state.id);
    const key = url.href;
    if (state.visited.has(key)) throw new Error('Repeated pagination URL');
    const controller = new AbortController();
    state.request = controller;
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(key, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Unable to load scenes');
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const incoming = all('[data-uni-v2-feed]', template.content).find(element => element.dataset.sectionId === state.id);
      const grid = incoming && query('[data-uni-v2-feed-grid]', incoming);
      if (!grid) throw new Error('Missing scene feed');
      all('[data-uni-v2-feed-item]', grid).forEach(item => {
        const id = item.dataset.uniV2FeedItem;
        if (!id || state.seen.has(id)) return;
        state.seen.add(id);
        state.queue.push(item);
      });
      state.next = grid.dataset.nextUrl || '';
      state.visited.add(key);
    } finally {
      clearTimeout(timeout);
      if (state.request === controller) state.request = null;
    }
  }

  async function loadFeed(state) {
    if (state.busy || state.disposed || state.loads >= 3) return;
    const initial = !state.initialComplete;
    const count = initial ? Math.max(0, 10 - state.grid.children.length) : 10;
    if (!count) {
      state.initialComplete = true;
      setFeedControls(state);
      return;
    }
    state.busy = true;
    state.status.textContent = state.element.dataset.loadingLabel;
    setFeedControls(state);
    try {
      while (state.queue.length < count && state.next && !state.disposed) await fetchFeedPage(state);
      if (state.disposed) return;
      const items = state.queue.splice(0, count);
      const fragment = document.createDocumentFragment();
      items.forEach(item => fragment.append(item));
      state.grid.append(fragment);
      state.initialComplete = true;
      if (!initial && items.length) state.loads += 1;
      state.status.textContent = !initial && items.length ? state.element.dataset.loadedLabel.replace('__count__', String(items.length)) : '';
      window.UniMobile?.initCardForms();
    } catch (error) {
      if (!state.disposed) state.status.textContent = state.element.dataset.errorLabel;
    } finally {
      if (!state.disposed) {
        const hadFocus = document.activeElement === state.button;
        state.busy = false;
        setFeedControls(state);
        if (hadFocus && state.button.hidden) state.link.focus({ preventScroll: true });
      }
    }
  }

  function initFeeds() {
    for (const [element, state] of feeds) {
      if (element.isConnected) continue;
      state.disposed = true;
      state.request?.abort();
      feeds.delete(element);
    }
    all('[data-uni-v2-feed]').forEach(element => {
      if (feeds.has(element)) return;
      const grid = query('[data-uni-v2-feed-grid]', element);
      const button = query('[data-uni-v2-feed-more]', element);
      const link = query('[data-uni-v2-feed-all]', element);
      if (!grid || !button || !link) return;
      const state = {
        element, grid, button, link, id: element.dataset.sectionId,
        status: query('[data-uni-v2-feed-status]', element), empty: query('[data-uni-v2-feed-empty]', element),
        label: button.textContent, next: grid.dataset.nextUrl || '', queue: [],
        seen: new Set(all('[data-uni-v2-feed-item]', grid).map(item => item.dataset.uniV2FeedItem)),
        visited: new Set(), loads: 0, busy: false, disposed: false, initialComplete: grid.children.length >= 10
      };
      feeds.set(element, state);
      button.addEventListener('click', () => loadFeed(state));
      setFeedControls(state);
      if (!state.initialComplete && state.next) loadFeed(state);
    });
  }

  function mountTabs() {
    const destination = query('.section-header .header-wrapper') || query('sticky-header');
    if (!destination) return;
    for (const [wrapper, marker] of mountedTabs) {
      if (wrapper.isConnected) continue;
      marker.remove();
      mountedTabs.delete(wrapper);
    }
    all('[data-uni-v2-tabs]').forEach(tabs => {
      const wrapper = tabs.closest('.shopify-section') || tabs;
      if (wrapper.contains(destination) || destination.contains(wrapper)) return;
      let marker = mountedTabs.get(wrapper);
      if (!marker) {
        marker = document.createComment('uni-home-tabs-position');
        wrapper.before(marker);
        mountedTabs.set(wrapper, marker);
      }
      destination.append(wrapper);
    });
    all('[data-uni-v2-tabs] [aria-current=page]').forEach(link => {
      const row = link.parentElement;
      row.scrollLeft += link.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - link.offsetWidth) / 2;
    });
    const sticky = destination.closest('sticky-header');
    if (typeof sticky?.setHeaderHeight === 'function') sticky.setHeaderHeight();
  }

  let countObserver;
  let observedCountHost;
  function syncCount() {
    const bubble = query('#cart-icon-bubble .cart-count-bubble');
    const value = bubble && !bubble.hidden ? (query('[aria-hidden]', bubble)?.textContent || bubble.textContent).trim() : '0';
    all('[data-uni-v2-count]').forEach(badge => {
      badge.textContent = value;
      badge.hidden = !value || value === '0';
    });
  }

  function watchCount() {
    if (!query('[data-uni-v2-count]')) {
      countObserver?.disconnect();
      observedCountHost = null;
      return;
    }
    const host = query('#cart-icon-bubble');
    if (host && host !== observedCountHost) {
      countObserver?.disconnect();
      observedCountHost = host;
      countObserver = new MutationObserver(syncCount);
      countObserver.observe(host, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
    }
    syncCount();
  }

  function initCart() {
    const dialog = query('#UniHomeCart');
    if (!dialog || carts.has(dialog)) return;
    const body = query('[data-uni-v2-cart-body]', dialog);
    if (!body) return;
    let status = query('[data-uni-v2-cart-status]', dialog);
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-uni-v2-cart-status', '');
      status.setAttribute('role', 'status');
      body.before(status);
    }
    const state = { body, status, busy: false, opener: null };
    carts.set(dialog, state);
    dialog.addEventListener('close', () => {
      document.body.classList.remove('uni-v2-cart-open');
      all('[data-uni-v2-cart]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (state.opener?.isConnected) state.opener.focus({ preventScroll: true });
    });
    let backdropDown = false;
    dialog.addEventListener('pointerdown', event => { backdropDown = event.target === dialog; });
    dialog.addEventListener('click', event => {
      if (backdropDown && event.target === dialog) dialog.close();
      backdropDown = false;
    });
  }

  async function openCart(opener) {
    const dialog = query('#UniHomeCart');
    const state = dialog && carts.get(dialog);
    if (!state || typeof dialog.showModal !== 'function' || !window.UniMobile?.renderCart) {
      location.assign(previewURL(root + 'cart'));
      return;
    }
    state.opener = opener;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('uni-v2-cart-open');
    opener.setAttribute('aria-expanded', 'true');
    if (state.busy || state.body.dataset.uniCartBusy === 'true' || state.body.dataset.uniCheckoutBusy === 'true') return;
    state.busy = true;
    state.body.inert = true;
    state.body.hidden = true;
    state.body.setAttribute('aria-busy', 'true');
    state.status.textContent = dialog.dataset.loadingLabel || 'Loading your cart…';
    try {
      let cart;
      if (window.UniOrderGift?.sync) cart = await window.UniOrderGift.sync();
      else {
        const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to load cart');
        cart = await response.json();
      }
      if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart');
      state.body.setAttribute('data-uni-cart-page', '');
      window.UniMobile.renderCart(state.body, cart);
      state.body.hidden = false;
      state.status.textContent = '';
      syncCount();
    } catch (error) {
      state.status.textContent = dialog.dataset.errorLabel || dialog.dataset.error || 'We couldn’t load your cart. Please close it and try again.';
    } finally {
      state.busy = false;
      state.body.inert = false;
      state.body.removeAttribute('aria-busy');
    }
  }

  function init() {
    mountTabs();
    initFeeds();
    initCart();
    watchCount();
  }

  document.addEventListener('click', event => {
    const close = event.target.closest('[data-uni-v2-cart-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const opener = event.target.closest('[data-uni-v2-cart]');
    if (!opener) return;
    event.preventDefault();
    openCart(opener);
  });
  document.addEventListener('shopify:section:unload', event => {
    for (const [wrapper, marker] of mountedTabs) {
      if (event.target !== wrapper && event.target.contains(wrapper) && marker.parentNode) marker.after(wrapper);
    }
    for (const [element, state] of feeds) {
      if (!event.target.contains(element)) continue;
      state.disposed = true;
      state.request?.abort();
      feeds.delete(element);
    }
    const dialog = query('#UniHomeCart', event.target);
    if (dialog?.open) dialog.close();
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', mountTabs);
  window.addEventListener('pageshow', init);
  window.UniHomeV2 = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
